import { Stack } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { DataStateView } from "@src/components/screen-state";
import { useCameraDetailsData } from "@src/features/cameras/hooks/useCameraDetailsData";
import { ArmDisarmPanelContent } from "@visioni/a3s-alarm-schedules/app";
import { TimePickerWith24Hour } from "@src/components/TimePickerWith24Hour";
import { getCurrentCameraArmedState, getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { isArmedFromArmingType } from "@src/features/schedules/utils/schedule-utils";
import { usePeriodicRefresh } from "@src/hooks/usePeriodicRefresh";
import { showSuccessToast } from "@src/utils/toast";
import { sizes } from "@src/constants/sizes";
import { useAlarmOverridePanelState, useSubmitAlarmOverride } from "@src/features/cameras/hooks";
import { CameraDetailsContent } from "@src/components/web/CameraDetailsContent.web";
import { ClipsSidePanel } from "@src/components/web/ClipsSidePanel.web";
import { RightDrawer } from "@src/components/web/RightDrawer.web";
import { WebLayout } from "@src/components/web/WebLayout.web";

export default function CameraDetailsScreen() {
  const currentTime = usePeriodicRefresh();
  const [isActionDrawerVisible, setIsActionDrawerVisible] = useState(false);
  const [actionMode, setActionMode] = useState<"override" | "adjust">("override");
  const {
    cameraKey,
    effectiveSiteId,
    effectiveCamera,
    isCameraLoading,
    isCameraFetching,
    cameraError,
    refetchCamera,
    alarmDetail,
    isAlarmLoading,
    isAlarmFetching,
    alarmError,
    refetchAlarm,
  } = useCameraDetailsData();

  const isLoading = isCameraLoading || isAlarmLoading;
  const isFetching = isCameraFetching || isAlarmFetching;
  const error = cameraError || alarmError;
  const refetch = () => {
    refetchCamera?.();
    refetchAlarm();
  };

  const armedState = useMemo(() => {
    if (!alarmDetail) return null;
    return getCurrentCameraArmedState(alarmDetail, currentTime);
  }, [alarmDetail, currentTime]);

  const isArmed = useMemo(() => {
    if (!armedState) return false;
    return isArmedFromArmingType(armedState.armingType);
  }, [armedState]);

  const { activeOverride: drawerActiveOverride, targetAction: drawerTargetAction } = useAlarmOverridePanelState({
    mode: actionMode,
    armedState,
    isArmed,
  });

  const handleArmDisarm = useCallback(() => {
    setActionMode("override");
    setIsActionDrawerVisible(true);
  }, []);

  const handleAdjust = useCallback(() => {
    setActionMode("adjust");
    setIsActionDrawerVisible(true);
  }, []);

  const { submitOverride: handleArmDisarmConfirm } = useSubmitAlarmOverride({
    mode: actionMode,
    alarmDetail,
    cameraKey,
    siteId: effectiveSiteId,
    onSuccess: ({ mode, override }) => {
      if (mode === "adjust") {
        showSuccessToast({ message: "Alarm extended", description: "Duration extended" });
      } else {
        const actionText = override.action === "armed" ? "armed" : "disarmed";
        showSuccessToast({ message: `Alarm ${actionText}`, description: "Override applied" });
      }
    },
  });

  const timezone = useMemo(
    () => getTimezoneFromAlarmDetail(alarmDetail) || Intl.DateTimeFormat().resolvedOptions().timeZone,
    [alarmDetail],
  );

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <WebLayout
        rightPanel={
          cameraKey ? <ClipsSidePanel cameraKey={cameraKey} siteId={effectiveSiteId} timezone={timezone} /> : null
        }
      >
        {effectiveCamera ?
          <CameraDetailsContent
            camera={effectiveCamera}
            alarmDetail={alarmDetail}
            siteId={effectiveSiteId}
            onArmDisarm={handleArmDisarm}
            onAdjust={handleAdjust}
            isArmDisarmLoading={false}
          />
        : <DataStateView
            data={effectiveCamera}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
            refetch={refetch}
            loadingMessage="Loading camera..."
            errorTitle="Camera not found"
            errorMessage={String(error) || "The requested camera could not be found"}
          >
            {() => null}
          </DataStateView>
        }
      </WebLayout>
      <RightDrawer
        visible={isActionDrawerVisible}
        onClose={() => setIsActionDrawerVisible(false)}
        title={actionMode === "adjust" ? "Adjust override" : "Temporary override"}
        width={sizes.webDrawerWidth + 120}
      >
        <ArmDisarmPanelContent
          mode={actionMode}
          targetAction={drawerTargetAction}
          activeOverride={drawerActiveOverride}
          timezone={timezone}
          onConfirm={(exception) => {
            handleArmDisarmConfirm(exception);
            setIsActionDrawerVisible(false);
          }}
          selectorWidth={180}
          renderTimePicker={({ isVisible, hour, minute, onConfirm, onCancel }) => (
            <TimePickerWith24Hour
              isVisible={isVisible}
              hour={hour}
              minute={minute}
              onConfirm={onConfirm}
              onCancel={onCancel}
            />
          )}
        />
      </RightDrawer>
    </>
  );
}
